/**
 * Where an account came from, as a bounded set of channel labels.
 *
 * Browser-safe like everything in this package: the SPA needs these names to
 * put one on a signup link, and the API needs them to validate what comes
 * back — one list, so the two cannot drift.
 *
 * **This is a marketing channel label, never a fact about a person.** Nothing
 * here is ever written to a user's record, or to any row keyed by a user. A
 * source is counted into a per-day total the moment an account is created and
 * is not associated with the account afterwards, which is what keeps it out of
 * the Privacy Policy's "What we collect from poll creators" list. See
 * `repos/signupSources.ts` for why that constraint is worth the design.
 *
 * The set is closed on purpose. These become DynamoDB attribute names on a
 * single item, so an open set lets anyone who can hit the signup endpoint
 * invent unbounded attributes and grow one row toward the 400 KB item limit.
 * Anything unrecognised folds into `other` rather than being dropped — a new
 * campaign still counts from day one, it just needs a slug added here before
 * it can be told apart from the rest.
 */

export const SIGNUP_SOURCES = [
  // No `via` on the signup link at all: typed the address, a bookmark, or any
  // route that does not set one.
  'direct',

  // Comparison pages. The four that do not exist yet are seeded deliberately:
  // they are already scheduled, and a slug missing on the day a page ships
  // sends its whole first week into `other`.
  'mentimeter-alternative',
  'slido-alternative',
  'poll-everywhere-alternative',
  'kahoot-alternative',
  'ahaslides-alternative',
  'vevox-alternative',

  // Use-case pages, slugged by their leaf rather than the full `/use-cases/`
  // path — the prefix is the same on every one of them and carries no signal.
  'town-halls',
  'conferences',
  'classrooms',
  'team-offsites',

  // Paid search, one per term group in the campaign brief. Seeded before the
  // test runs rather than after: paid is the only channel here that spends
  // money per click, and it is the one that must never be indistinguishable
  // from organic direct traffic.
  'ads-alternative',
  'ads-category',
  'ads-event',

  // The Product Hunt launch (2026-08-31). Seeded the night before for the
  // same reason as ads-*: the listing link must carry `?via=producthunt`
  // from the moment it goes live, or the one loud launch this plan is built
  // around is uncountable as its own channel on day one.
  'producthunt',

  // Directory listings. Seeded before any listing goes live: a `via` this
  // build does not know folds into `other` permanently, and a listing's
  // website URL is hard to change later on some directories. `directory` is
  // the catch-all for SaaSHub and anything smaller listed later.
  'alternativeto',
  'g2',
  'capterra',
  'directory',

  // The join screen. Every free room carries the FloorPoll mark, so a voter
  // who becomes a presenter arrives this way — the one channel whose reach
  // grows with usage, and the reason any of this is worth measuring.
  'join',

  // Recognised as a real signup, from a `via` this build does not know.
  'other',
] as const;

export type SignupSource = (typeof SIGNUP_SOURCES)[number];

export function isSignupSource(value: unknown): value is SignupSource {
  return typeof value === 'string' && (SIGNUP_SOURCES as readonly string[]).includes(value);
}

/**
 * The label to count for a `via` value that arrived from a browser.
 *
 * Total rather than partial: every signup lands on exactly one slug, so the
 * per-source counts always sum to the number of accounts created. A caller
 * that dropped unknown values instead would leave the columns silently
 * failing to add up to `signupsPerWeek`, which is the number this whole
 * mechanism exists to explain.
 *
 * Absent means `direct`; unrecognised means `other`. The two are kept apart
 * because they mean opposite things — nobody claimed a channel, versus a
 * channel claimed one this build cannot name.
 */
export function signupSourceFrom(via: string | null | undefined): SignupSource {
  if (via === null || via === undefined || via.trim().length === 0) return 'direct';
  return isSignupSource(via) ? via : 'other';
}

/**
 * The first UTC day the signup counters were running in production.
 *
 * Prod deploys when a release is *published*, not when its tag is pushed, and
 * the counters shipped in v1.7.3 — published 2026-08-30T00:14:31Z, with the
 * two-pass deploy finishing minutes later on the same UTC day these rows are
 * keyed by. Every day the dashboard draws before this one is a structural
 * zero: nothing was counted, which is not the same finding as nobody signing
 * up, and the panel says so rather than letting a run of empty columns read as
 * a dead channel.
 *
 * A constant rather than a value derived from the earliest row, because the
 * two disagree exactly when it matters: a window with no rows at all has no
 * earliest row to derive from, and that is the case — a quiet first week —
 * where a confident zero does the most damage. Same reasoning as
 * `METRICS_EPOCH` in the API's months.ts.
 */
export const SIGNUP_COUNTING_FROM = '2026-08-30';

/** Display names for the operator dashboard. */
export const SIGNUP_SOURCE_LABELS: Record<SignupSource, string> = {
  direct: 'Direct',
  'mentimeter-alternative': 'vs. Mentimeter',
  'slido-alternative': 'vs. Slido',
  'poll-everywhere-alternative': 'vs. Poll Everywhere',
  'kahoot-alternative': 'vs. Kahoot',
  'ahaslides-alternative': 'vs. AhaSlides',
  'vevox-alternative': 'vs. Vevox',
  'town-halls': 'Town halls',
  conferences: 'Conferences',
  classrooms: 'Classrooms',
  'team-offsites': 'Team offsites',
  'ads-alternative': 'Paid — alternative terms',
  'ads-category': 'Paid — category terms',
  'ads-event': 'Paid — event terms',
  producthunt: 'Product Hunt',
  alternativeto: 'AlternativeTo',
  g2: 'G2',
  capterra: 'Capterra',
  directory: 'Directory (SaaSHub and smaller directories)',
  join: 'Join screen',
  other: 'Other',
};
